import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { get, post } from "../api/client";
import { useAuth } from "../state";
import i18n from "../i18n";
import { LanguageSwitcher } from "../components/language-switcher";
import type { Ninja, NinjaAccessStatus } from "../types";
import { DevLoginPanel } from "./dev-login";

const API_BASE_URL = import.meta.env.VITE_API_URL ||
  (window.location.hostname.includes("bux.codes") ? "https://api.bux.codes/api" : "/api");

// Default fallback coordinates (used when geolocation unavailable over HTTP)
const DEFAULT_LAT = 0;
const DEFAULT_LON = 0;

function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

function countStatuses(node: unknown): { done: number; total: number } {
  let total = 0;
  let done = 0;
  const visit = (value: any) => {
    if (!value || typeof value !== "object") return;
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (typeof value.status === "boolean") {
      total += 1;
      if (value.status) done += 1;
    }
    Object.values(value).forEach(visit);
  };
  visit(node);
  return { done, total };
}

function requestLocation(): Promise<{ lat: number; lon: number }> {
  return new Promise((resolve) => {
    // Check if we're on a secure origin (HTTPS or localhost)
    const isSecure =
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    // Location is a background detail — fall back silently rather than
    // showing kids confusing geolocation messages.
    if (!isSecure || !navigator.geolocation) {
      resolve({ lat: DEFAULT_LAT, lon: DEFAULT_LON });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => resolve({ lat: DEFAULT_LAT, lon: DEFAULT_LON }),
      { timeout: 5000 },
    );
  });
}

interface LoginSuccess {
  facilityId: string;
  studentId: string;
  token: string;
  ninja: Ninja;
}

interface LoginLocked {
  facilityId: string;
  studentId: string;
  token: string;
  locked: true;
}

type LoginResult = LoginSuccess | LoginLocked | { error: string };

/**
 * The full CN login flow: authenticate, look up the current activity/level,
 * then sync the profile into NinjaBux. `onProgress` reports the friendly
 * step-by-step status messages shown under the form.
 */
async function performLogin(
  user: string,
  coords: { lat: number; lon: number },
  onProgress: (message: string) => void,
): Promise<LoginResult> {
  const loginRes = await post<any>("/cn/login", {
    user,
    latitude: Number(coords.lat),
    longitude: Number(coords.lon),
  });

  if (loginRes.error || !loginRes.data) {
    const friendlyError =
      loginRes.error && /404|not found/i.test(loginRes.error)
        ? i18n.t("login.usernameNotFound")
        : loginRes.error || i18n.t("login.generic");
    return { error: friendlyError };
  }

  let loginData = loginRes.data as any;
  if (typeof loginData === "string") {
    try {
      loginData = JSON.parse(loginData);
    } catch {
      // leave as string
    }
  }

  const token = loginData.token;
  if (!token) {
    return { error: i18n.t("login.noToken") };
  }

  const claims = decodeJwt(token);
  const facilityId = claims?.["facilityid"] as string | undefined;
  const studentId = claims?.["oid"] as string | undefined;
  if (!facilityId || !studentId) {
    return { error: i18n.t("login.badToken") };
  }

  const accessStatus = await get<NinjaAccessStatus>(
    `/facilities/${facilityId}/ninjas/${studentId}/access-status`,
    { Authorization: `Bearer ${token}` },
  );
  if (accessStatus.data?.locked) {
    return { facilityId, studentId, token, locked: true };
  }

  onProgress(i18n.t("login.findingProfile"));
  const activityRes = await fetch(`${API_BASE_URL}/cn/activity/current`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!activityRes.ok) {
    const errText = await activityRes.text();
    console.error("Activity fetch failed:", activityRes.status, errText);
    return { error: i18n.t("login.progressFailed") };
  }
  const activityData = await activityRes.json();
  const rel = activityData?.relationShips?.data || {};
  if (rel.programId) {
    sessionStorage.setItem("cn_programId", rel.programId);
  }
  if (rel.courseId) {
    sessionStorage.setItem("cn_courseId", rel.courseId);
  }
  if (rel.levelId) {
    sessionStorage.setItem("cn_levelId", rel.levelId);
  }

  // Get level status to derive level sequence and activity sequence
  let levelSequence: number | null = null;
  let activitySequence: number | null = null;
  let completedSteps: number | null = null;
  let totalSteps: number | null = null;
  try {
    const params = new URLSearchParams({
      programId: rel.programId || "",
      courseId: rel.courseId || "",
    });
    if (rel.levelId) {
      params.append("levelId", rel.levelId);
    }
    const statusRes = await fetch(
      `${API_BASE_URL}/cn/level/statusinfo?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (statusRes.ok) {
      const statusData = await statusRes.json();
      levelSequence = statusData.levelSequence ?? null;
      completedSteps = statusData.completedSteps ?? null;
      totalSteps = statusData.totalSteps ?? null;

      // Extract activity sequence from the activitySequences map
      const currentActivityId = rel.activityId || activityData.id;
      if (statusData.activitySequences && currentActivityId) {
        activitySequence =
          statusData.activitySequences[currentActivityId] ?? null;
      }
    }
  } catch {
    // ignore
  }

  // Try group-level status for finer-grained step counts
  if (rel.programId && rel.courseId) {
    try {
      const groupParams = new URLSearchParams({
        programId: rel.programId,
        courseId: rel.courseId,
      });
      const groupRes = await fetch(
        `${API_BASE_URL}/cn/groups/current?${groupParams.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (groupRes.ok) {
        const raw = await groupRes.text();
        let groupData: any;
        try {
          groupData = JSON.parse(raw);
        } catch {
          groupData = raw;
        }
        const { done, total } = countStatuses(groupData);
        if (total > 0) {
          completedSteps = done;
          totalSteps = total;
        }
      }
    } catch {
      // ignore
    }
  }

  onProgress(i18n.t("login.almostThere"));
  const syncRes = await post<any>(
    `/facilities/${facilityId}/ninjas/${studentId}/sync-local`,
    {
      firstName: loginData.user?.firstName || rel.firstName || "",
      lastName: loginData.user?.lastName || rel.lastName || "",
      courseName: rel.courseName || "",
      levelId: rel.levelId || "",
      levelSequence: levelSequence,
      activityId: rel.activityId || activityData.id,
      activitySequence: activitySequence,
      groupId: rel.groupId || "",
      subGroupId: rel.subgroupId || "",
      completedSteps: completedSteps,
      totalSteps: totalSteps,
      lastModifiedDate: activityData.lastModifiedDate || null,
      facilityName: loginData.user?.facilityName || null,
    },
    { Authorization: `Bearer ${token}` },
  );
  if (syncRes.error || !syncRes.data) {
    console.error("Sync failed:", syncRes.error);
    return { error: i18n.t("login.saveFailed") };
  }

  const syncData = syncRes.data as any;
  const ninjaData = syncData?.changes?.ninja;

  const ninja: Ninja = {
    id: ninjaData?.id || 0,
    studentId: ninjaData?.studentId || studentId,
    firstName:
      ninjaData?.firstName || loginData.user?.firstName || rel.firstName || "",
    lastName:
      ninjaData?.lastName || loginData.user?.lastName || rel.lastName || "",
    courseName: ninjaData?.courseName || rel.courseName || "",
    levelName: ninjaData?.levelName || null,
    levelSequence: ninjaData?.levelSequence ?? levelSequence ?? null,
    activityId:
      ninjaData?.activityId || rel.activityId || activityData.id || null,
    groupId: ninjaData?.groupId || rel.groupId || null,
    subGroupId: ninjaData?.subGroupId || rel.subgroupId || null,
    completedSteps: ninjaData?.completedSteps ?? completedSteps ?? null,
    totalSteps: ninjaData?.totalSteps ?? totalSteps ?? null,
    currentBalance: ninjaData?.currentBalance ?? 0,
    lastSyncedAt: ninjaData?.lastSyncedAt || new Date().toISOString(),
    locked: ninjaData?.locked ?? false,
  };

  return { facilityId, studentId, token, ninja };
}

export function LoginPage() {
  const { t } = useTranslation();
  const { startSession, setCurrentNinja, setLocked } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [isError, setIsError] = useState(false);

  // Request location immediately on page load so the fix is (usually) ready
  // by the time the ninja submits.
  const coordsRef = useRef<{ lat: number; lon: number } | null>(null);
  useEffect(() => {
    let cancelled = false;
    requestLocation().then((coords) => {
      if (!cancelled) coordsRef.current = coords;
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(evt: FormEvent) {
    evt.preventDefault();
    if (busy) return;
    const user = username.trim();

    if (!user) {
      setStatus(t("login.enterUsername"));
      setIsError(true);
      return;
    }

    setBusy(true);
    setStatus("");
    setIsError(false);

    try {
      const coords = coordsRef.current ?? (await requestLocation());
      const result = await performLogin(user, coords, (message) => {
        setStatus(message);
        setIsError(false);
      });

      if ("error" in result) {
        setStatus(result.error);
        setIsError(true);
        return;
      }

      sessionStorage.setItem("cn_token", result.token);
      startSession({
        facilityId: result.facilityId,
        studentId: result.studentId,
      });
      if (!("ninja" in result)) {
        setCurrentNinja(null);
        setLocked(true);
        navigate("/dashboard");
        return;
      }
      setCurrentNinja(result.ninja);
      navigate("/dashboard");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="login-page">
        <div className="login-card glass-card">
          <header className="login-header text-center">
            <img
              src="/CodeNinjasLogo.svg"
              alt="Code Ninjas"
              className="login-logo impact-logo"
            />
            <h1 className="login-brand">{t("common.appName")}</h1>
          </header>

          <form className="login-form" onSubmit={handleSubmit}>
            <input
              type="text"
              name="cn-username"
              className="login-input"
              placeholder={t("login.usernamePlaceholder")}
              aria-label={t("login.usernamePlaceholder")}
              autoComplete="username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />

            <button type="submit" className="btn btn-green" disabled={busy}>
              {busy ? t("login.loggingIn") : t("login.logIn")}
            </button>
          </form>

          <div
            className={`login-status ${isError ? "is-error" : ""}`}
            style={{ display: status ? "block" : "none" }}
            role="status"
            aria-live="polite"
          >
            {status}
          </div>
          <LanguageSwitcher />
        </div>
      </div>
      {/* Dev-only quick login. Stripped from production builds (see ./dev-login.tsx). */}
      {import.meta.env.DEV && <DevLoginPanel />}
    </>
  );
}
